// WORKOUTS — the pillar's RESULT SHAPE, written by the BL through
// `writeWorkoutsAttachItem` and read by the FE through
// `readWorkoutsAttachItem`.
//
// The vendor signs its demonstration-video URLs with a hard 48-hour expiry, so
// `video` and `demo` carry the DURABLE `/media/exercise-video/<handle>`
// address instead, and the READER refuses anything else — a signed CDN URL
// that reached a block would render as a working player today and a 403 on
// Thursday.
//
// Only the prescription folds, and the vendor's repetition string (`8-12`,
// `30 seconds`) is printed VERBATIM: the two shapes arrive on slots of one
// plan, so parsing one into a number would make a warm-up hold read as three
// repetitions.
//
// ─── CENSUS (D28), from `ymove/examples.json` ───────────────────────────────
//
//   Exercise   exerciseId·slug·title·description·instructions·importantPoints·
//     muscleGroup·equipment·category·difficulty·exerciseTypes 1/1 on the
//     `ymove.exerciseDetail` capture (`boat-pose`: 4 instructions, 3 important
//     points, 3 exercise types) and on every row of `ymove.exerciseSearch`.
//     `secondaryMuscles` is EMPTY on that capture — an absent secondary set
//     prints no line at all rather than a blank one. `videoDurationSecs` is
//     null off the list path by construction and null on the captured detail,
//     so `video seconds` is carried as the optional it is and has never been
//     observed populated.
//   Workout    name·difficulty·estimatedMinutes·muscleGroups·mainExercises 1/1
//     (`ymove.workoutGenerator` `Chest, Triceps Workout`: 20 estimated minutes,
//     slots carrying order·sets·repetitions·restSeconds·slug·title). The
//     captured plan runs a warm-up and a main block and an EMPTY cool-down.
// ════════════════════════════════════════════════════════════════════════════

import { canonicalExerciseVideoAddress } from '../../../business-logic/domains/serpers/vendor-media.url.js';
import type { AttachFieldLine, AttachItemBlock } from '../grammar.js';
import { attachFieldValue } from '../parse-attach-document.js';
import {
  attachFieldGroups,
  attachGroupValue,
  attachNumberOf,
  attachNumberText,
  attachPartNumber,
  attachPartText,
  attachSubRecordOf,
  attachSubRecordText,
  attachTextOf,
} from './attach-value-codec.js';

/** WK's closed field vocabulary — both records, one spelling each. */
export enum WorkoutsAttachField {
  /** What the movement or the session is called. */
  Name = 'name',
  /** A muscle the work lands on. REPEATED: one primary on an exercise, the
   *  plan's whole target set on a workout. */
  Muscle = 'muscle',
  /** A muscle the movement also works. REPEATED, exercise only. */
  SecondaryMuscle = 'secondary muscle',
  /** What the movement needs to be performed at all. */
  Equipment = 'equipment',
  /** The catalog's own difficulty tier, on either record. */
  Difficulty = 'difficulty',
  /** What kind of training it is — strength, yoga, mobility. REPEATED. */
  Training = 'training',
  /** The catalog's filing category. */
  Category = 'category',
  /** The movement in the catalog's own words. Free text, its own line. */
  Description = 'description',
  /** One step of the movement, in the order it is performed. REPEATED. */
  Step = 'step',
  /** One thing that decides whether the form is right. REPEATED. */
  FormPoint = 'form point',
  /** The demonstration video — a durable address that resolves a live URL when
   *  it is opened, never a signed URL that dies in 48 hours. A value that is
   *  not one reads back as absent. */
  Video = 'video',
  /** How long the clip runs, in seconds, where the record carries it. */
  VideoSeconds = 'video seconds',
  /** How long the whole session takes, in minutes. Workout only. */
  Minutes = 'minutes',
  /** One movement of the session. REPEATED, and it opens a GROUP: the `phase`,
   *  `prescribed` and `demo` lines under it are its own. */
  Exercise = 'exercise',
  /** Which block of the session the movement above belongs to. */
  Phase = 'phase',
  /** What the session prescribes for the movement above. */
  Prescribed = 'prescribed',
  /** The movement above's demonstration video — the same durable address. */
  Demo = 'demo',
  /** The durable link the writer attached this row under. A workouts record
   *  carries no public page of its own. */
  Link = 'link',
}

/** The parts of the one sub-record this pillar writes. */
export enum WorkoutsAttachPart {
  Sets = 'sets',
  /** The vendor's prescription VERBATIM — a range or a duration. */
  Reps = 'reps',
  RestSeconds = 'rest seconds',
}

/** The generator's own three blocks, read back by exact match. */
export enum WorkoutsAttachPhase {
  Warmup = 'warm-up',
  Main = 'main',
  Cooldown = 'cool-down',
}

const WORKOUTS_PHASES: ReadonlySet<string> = new Set<string>(
  Object.values(WorkoutsAttachPhase),
);

/** Which of the pillar's two records an item is. */
export enum WorkoutsAttachRecordKind {
  Exercise = 'exercise',
  Workout = 'workout',
}

// ─── the typed shape ────────────────────────────────────────────────────────

/** One catalog movement, typed. */
export interface WorkoutsAttachExercise {
  readonly record: WorkoutsAttachRecordKind.Exercise;
  readonly name: string;
  readonly description: string | null;
  /** The primary muscle, in the catalog's own vocabulary (`full_body`). */
  readonly muscle: string | null;
  readonly secondaryMuscles: readonly string[];
  readonly equipment: string | null;
  readonly difficulty: string | null;
  readonly trainingTypes: readonly string[];
  readonly category: string | null;
  readonly steps: readonly string[];
  readonly formPoints: readonly string[];
  /** The durable `/media/exercise-video/<handle>` address, or null. */
  readonly videoUrl: string | null;
  readonly videoSeconds: number | null;
  readonly linkUrl: string | null;
}

/** One movement of a generated session, with what the session asks of it. */
export interface WorkoutsAttachSlot {
  readonly name: string;
  readonly phase: WorkoutsAttachPhase | null;
  readonly sets: number | null;
  /** The vendor's prescription verbatim. A string because `8-12` and
   *  `30 seconds` arrive on slots of one plan. */
  readonly repetitions: string | null;
  readonly restSeconds: number | null;
  readonly videoUrl: string | null;
}

/** One generated session, typed. */
export interface WorkoutsAttachWorkout {
  readonly record: WorkoutsAttachRecordKind.Workout;
  readonly name: string;
  readonly muscles: readonly string[];
  readonly difficulty: string | null;
  readonly minutes: number | null;
  readonly slots: readonly WorkoutsAttachSlot[];
  readonly linkUrl: string | null;
}

/** One workouts row, typed. The object a widget renders from. */
export type WorkoutsAttachResult = WorkoutsAttachExercise | WorkoutsAttachWorkout;

// ─── the headline ───────────────────────────────────────────────────────────

/** The human read of one row — DISPLAY ONLY. */
export function workoutsAttachHeadline(input: {
  result: WorkoutsAttachResult;
}): string {
  return input.result.name;
}

// ─── writing ────────────────────────────────────────────────────────────────

function textLine(input: {
  key: WorkoutsAttachField;
  value: string | null;
}): AttachFieldLine[] {
  const value = attachTextOf({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

function numberLine(input: {
  key: WorkoutsAttachField;
  value: number | null;
}): AttachFieldLine[] {
  const value = input.value === null ? null : attachNumberText({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

function textLines(input: {
  key: WorkoutsAttachField;
  values: readonly string[];
}): AttachFieldLine[] {
  return input.values.flatMap((value) => textLine({ key: input.key, value }));
}

function slotLines(input: { slot: WorkoutsAttachSlot }): AttachFieldLine[] {
  const slot = input.slot;
  const prescribed = attachSubRecordText({
    parts: [
      {
        key: WorkoutsAttachPart.Sets,
        value: slot.sets === null ? null : attachNumberText({ value: slot.sets }),
      },
      { key: WorkoutsAttachPart.Reps, value: slot.repetitions },
      {
        key: WorkoutsAttachPart.RestSeconds,
        value:
          slot.restSeconds === null
            ? null
            : attachNumberText({ value: slot.restSeconds }),
      },
    ],
  });
  return [
    ...textLine({ key: WorkoutsAttachField.Exercise, value: slot.name }),
    ...textLine({ key: WorkoutsAttachField.Phase, value: slot.phase }),
    ...textLine({ key: WorkoutsAttachField.Prescribed, value: prescribed }),
    ...textLine({ key: WorkoutsAttachField.Demo, value: slot.videoUrl }),
  ];
}

function exerciseFields(input: {
  result: WorkoutsAttachExercise;
}): AttachFieldLine[] {
  const result = input.result;
  return [
    ...textLine({ key: WorkoutsAttachField.Name, value: result.name }),
    ...textLine({ key: WorkoutsAttachField.Muscle, value: result.muscle }),
    ...textLines({
      key: WorkoutsAttachField.SecondaryMuscle,
      values: result.secondaryMuscles,
    }),
    ...textLine({ key: WorkoutsAttachField.Equipment, value: result.equipment }),
    ...textLine({ key: WorkoutsAttachField.Difficulty, value: result.difficulty }),
    ...textLines({
      key: WorkoutsAttachField.Training,
      values: result.trainingTypes,
    }),
    ...textLine({ key: WorkoutsAttachField.Category, value: result.category }),
    ...textLine({ key: WorkoutsAttachField.Description, value: result.description }),
    ...textLines({ key: WorkoutsAttachField.Step, values: result.steps }),
    ...textLines({ key: WorkoutsAttachField.FormPoint, values: result.formPoints }),
    ...textLine({ key: WorkoutsAttachField.Video, value: result.videoUrl }),
    ...numberLine({
      key: WorkoutsAttachField.VideoSeconds,
      value: result.videoSeconds,
    }),
    ...textLine({ key: WorkoutsAttachField.Link, value: result.linkUrl }),
  ];
}

function workoutFields(input: {
  result: WorkoutsAttachWorkout;
}): AttachFieldLine[] {
  const result = input.result;
  return [
    ...textLine({ key: WorkoutsAttachField.Name, value: result.name }),
    ...textLines({ key: WorkoutsAttachField.Muscle, values: result.muscles }),
    ...textLine({ key: WorkoutsAttachField.Difficulty, value: result.difficulty }),
    ...numberLine({ key: WorkoutsAttachField.Minutes, value: result.minutes }),
    ...textLine({ key: WorkoutsAttachField.Link, value: result.linkUrl }),
    ...result.slots.flatMap((slot) => slotLines({ slot })),
  ];
}

/** One typed workouts row as a block item. */
export function writeWorkoutsAttachItem(input: {
  result: WorkoutsAttachResult;
}): AttachItemBlock {
  const result = input.result;
  return {
    headline: workoutsAttachHeadline({ result }),
    fields:
      result.record === WorkoutsAttachRecordKind.Exercise
        ? exerciseFields({ result })
        : workoutFields({ result }),
  };
}

// ─── reading ────────────────────────────────────────────────────────────────

function fieldText(input: {
  item: AttachItemBlock;
  key: WorkoutsAttachField;
}): string | null {
  return attachTextOf({ value: attachFieldValue({ item: input.item, key: input.key }) });
}

function fieldNumber(input: {
  item: AttachItemBlock;
  key: WorkoutsAttachField;
}): number | null {
  return attachNumberOf({ value: fieldText(input) });
}

/**
 * A demonstration address, or null for anything that is not one. What comes
 * back is OUR spelling of it, never the text that was read.
 */
function videoAddressOf(input: { value: string | null }): string | null {
  const value = input.value;
  if (value === null) return null;
  return canonicalExerciseVideoAddress({ value });
}

function fieldValues(input: {
  item: AttachItemBlock;
  key: WorkoutsAttachField;
}): string[] {
  return input.item.fields
    .filter((field) => field.key === input.key)
    .flatMap((field) => {
      const value = attachTextOf({ value: field.value });
      return value === null ? [] : [value];
    });
}

/** The phase a line names, or null where it named one outside the
 *  generator's own three. */
export function workoutsAttachPhaseOf(input: {
  value: string | null;
}): WorkoutsAttachPhase | null {
  const value = attachTextOf({ value: input.value });
  return value !== null && WORKOUTS_PHASES.has(value)
    ? (value as WorkoutsAttachPhase)
    : null;
}

function slotsOf(input: { item: AttachItemBlock }): WorkoutsAttachSlot[] {
  return attachFieldGroups({
    item: input.item,
    openKey: WorkoutsAttachField.Exercise,
    memberKeys: [
      WorkoutsAttachField.Phase,
      WorkoutsAttachField.Prescribed,
      WorkoutsAttachField.Demo,
    ],
  }).flatMap((group) => {
    const name = attachTextOf({ value: group.open });
    if (name === null) return [];
    const prescribed = attachSubRecordOf({
      value: attachGroupValue({ group, key: WorkoutsAttachField.Prescribed }),
    });
    return [
      {
        name,
        phase: workoutsAttachPhaseOf({
          value: attachGroupValue({ group, key: WorkoutsAttachField.Phase }),
        }),
        sets: attachPartNumber({
          record: prescribed,
          key: WorkoutsAttachPart.Sets,
        }),
        repetitions: attachPartText({
          record: prescribed,
          key: WorkoutsAttachPart.Reps,
        }),
        restSeconds: attachPartNumber({
          record: prescribed,
          key: WorkoutsAttachPart.RestSeconds,
        }),
        videoUrl: videoAddressOf({
          value: attachTextOf({
            value: attachGroupValue({ group, key: WorkoutsAttachField.Demo }),
          }),
        }),
      },
    ];
  });
}

/** Whether an item is the generated-session record: the field set IS the shape. */
function isWorkoutItem(input: { item: AttachItemBlock }): boolean {
  return input.item.fields.some(
    (field) =>
      field.key === WorkoutsAttachField.Minutes ||
      field.key === WorkoutsAttachField.Exercise,
  );
}

/**
 * One block item, read as a typed workouts row — or null when it carries no
 * name. THE HEADLINE IS NOT READ; `name:` is.
 */
export function readWorkoutsAttachItem(input: {
  item: AttachItemBlock;
}): WorkoutsAttachResult | null {
  const item = input.item;
  const name = fieldText({ item, key: WorkoutsAttachField.Name });
  if (name === null) return null;
  const linkUrl = fieldText({ item, key: WorkoutsAttachField.Link });
  if (isWorkoutItem({ item })) {
    return {
      record: WorkoutsAttachRecordKind.Workout,
      name,
      muscles: fieldValues({ item, key: WorkoutsAttachField.Muscle }),
      difficulty: fieldText({ item, key: WorkoutsAttachField.Difficulty }),
      minutes: fieldNumber({ item, key: WorkoutsAttachField.Minutes }),
      slots: slotsOf({ item }),
      linkUrl,
    };
  }
  return {
    record: WorkoutsAttachRecordKind.Exercise,
    name,
    description: fieldText({ item, key: WorkoutsAttachField.Description }),
    muscle: fieldText({ item, key: WorkoutsAttachField.Muscle }),
    secondaryMuscles: fieldValues({
      item,
      key: WorkoutsAttachField.SecondaryMuscle,
    }),
    equipment: fieldText({ item, key: WorkoutsAttachField.Equipment }),
    difficulty: fieldText({ item, key: WorkoutsAttachField.Difficulty }),
    trainingTypes: fieldValues({ item, key: WorkoutsAttachField.Training }),
    category: fieldText({ item, key: WorkoutsAttachField.Category }),
    steps: fieldValues({ item, key: WorkoutsAttachField.Step }),
    formPoints: fieldValues({ item, key: WorkoutsAttachField.FormPoint }),
    videoUrl: videoAddressOf({
      value: fieldText({ item, key: WorkoutsAttachField.Video }),
    }),
    videoSeconds: fieldNumber({ item, key: WorkoutsAttachField.VideoSeconds }),
    linkUrl,
  };
}
